import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { MapContainer, Circle, CircleMarker, Marker, Polygon, TileLayer, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getRequestWithToken } from "../../utils/ApiRequests";
import { geoJsonToLeaflet, geoPointToLeaflet, getPolygonCenter } from "../../utils/geoUtils";
import { checkPermission } from "../../utils/userPermissions";
import Unauthorized from "../../components/unauthorized/Unauthorized";
import ReusableLoader from "../../components/ReusableLoader/ReusableLoader";

const ROLE_COLOR_PALETTE = [
  "#e53935", "#8e24aa", "#1e88e5", "#00897b",
  "#43a047", "#f4511e", "#6d4c41", "#039be5",
  "#3949ab", "#00acc1", "#7cb342", "#fb8c00",
];

const _roleColorCache = {};
let _roleColorIndex = 0;
function getRoleColor(role) {
  const key = role || "unknown";
  if (!_roleColorCache[key]) {
    _roleColorCache[key] = ROLE_COLOR_PALETTE[_roleColorIndex++ % ROLE_COLOR_PALETTE.length];
  }
  return _roleColorCache[key];
}

function getRoleInitials(role) {
  if (!role) return "?";
  const words = role.trim().split(/[\s_-]+/);
  return words.length === 1 ? words[0].slice(0, 2).toUpperCase() : (words[0][0] + words[1][0]).toUpperCase();
}

// Icon cache — avoids creating a new L.divIcon object on every render
const _iconCache = {};
function createRoleMarkerIcon(role, overrideColor) {
  const key = `${role || "unknown"}__${overrideColor || "default"}`;
  if (_iconCache[key]) return _iconCache[key];
  const color = overrideColor || getRoleColor(role);
  const initials = getRoleInitials(role);
  _iconCache[key] = L.divIcon({
    className: "",
    html: `<div style="background:${color};width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:11px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.45);font-family:sans-serif;line-height:1;">${initials}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18],
  });
  return _iconCache[key];
}

const RADIUS_MARKS = [
  { value: 5, label: "5km" },
  { value: 25, label: "25km" },
  { value: 50, label: "50km" },
  { value: 70, label: "70km" },
];

// Cluster icon factories — defined outside component so they are stable references
function insideClusterIcon(cluster) {
  const count = cluster.getChildCount();
  return L.divIcon({
    className: "",
    html: `<div style="background:#2e7d32;color:#fff;border-radius:50%;width:38px;height:38px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35);">${count}</div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

function outsideClusterIcon(cluster) {
  const count = cluster.getChildCount();
  return L.divIcon({
    className: "",
    html: `<div style="background:#e65100;color:#fff;border-radius:50%;width:38px;height:38px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35);">${count}</div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

function computeCentroid(positions) {
  const ring = Array.isArray(positions[0][0]) ? positions[0] : positions;
  let latSum = 0, lngSum = 0;
  ring.forEach(([lat, lng]) => { latSum += lat; lngSum += lng; });
  return [latSum / ring.length, lngSum / ring.length];
}

function FitBoundsToPolygon({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length === 0) return;
    try {
      const flat = Array.isArray(positions[0][0]) ? positions.flat(1) : positions;
      if (flat.length > 0) {
        const bounds = L.latLngBounds(flat);
        if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30] });
      }
    } catch (_) { }
  }, [positions, map]);
  return null;
}

// HeatmapLayer — canvas-based density map using leaflet.heat
// Dynamic import + window.L workaround for Vite ESM compatibility
function HeatmapLayer({ insidePoints, outsidePoints }) {
  const map = useMap();
  const layersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    layersRef.current.forEach(l => { try { map.removeLayer(l); } catch (_) { } });
    layersRef.current = [];

    if (insidePoints.length === 0 && outsidePoints.length === 0) return;

    window.L = L; // leaflet.heat accesses window.L at runtime
    import("leaflet.heat").then(() => {
      if (cancelled) return;
      const newLayers = [];
      if (insidePoints.length > 0) {
        const inside = window.L.heatLayer(insidePoints, {
          radius: 20, blur: 20, minOpacity: 0.4,
          gradient: { 0.3: "#a5d6a7", 0.6: "#43a047", 1.0: "#1b5e20" },
        });
        inside.addTo(map);
        newLayers.push(inside);
      }
      if (outsidePoints.length > 0) {
        const outside = window.L.heatLayer(outsidePoints, {
          radius: 20, blur: 20, minOpacity: 0.4,
          gradient: { 0.3: "#ffcc80", 0.6: "#f57c00", 1.0: "#bf360c" },
        });
        outside.addTo(map);
        newLayers.push(outside);
      }
      layersRef.current = newLayers;
    });

    return () => {
      cancelled = true;
      layersRef.current.forEach(l => { try { map.removeLayer(l); } catch (_) { } });
      layersRef.current = [];
    };
  }, [insidePoints, outsidePoints, map]);

  return null;
}

const RegionMapView = () => {
  const canRead = checkPermission("HC-Addon");

  const [regions, setRegions] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [radiusKm, setRadiusKm] = useState(25);
  const [viewMode, setViewMode] = useState("cluster"); // "cluster" | "heatmap"
  const [initialLoading, setInitialLoading] = useState(true);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendCounts, setBackendCounts] = useState({ insideCount: 0, outsideCount: 0, totalCount: 0 });
  const candidateFetchCounter = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const fetchInitial = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const [regionsRes, mappingsRes] = await Promise.all([
          getRequestWithToken("/admin/regions?page=1&limit=200"),
          getRequestWithToken("/admin/region-form-mappings?page=1&limit=200"),
        ]);
        if (!cancelled) {
          setRegions((regionsRes?.data?.data?.regions || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
          setMappings((mappingsRes?.data?.data?.mappings || []).sort((a, b) => (a.form_id?.role || "").localeCompare(b.form_id?.role || "")));
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.msg || err?.message || "Failed to load initial data.");
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };
    fetchInitial();
    return () => { cancelled = true; };
  }, []);

  // Single API call — passes regionId so backend classifies inside/outside server-side
  const handleLoadCandidates = useCallback(async () => {
    if (!selectedRegion) return;
    const fetchId = ++candidateFetchCounter.current;
    setCandidatesLoading(true);
    setError(null);
    setCandidatesLoaded(false);
    try {
      const polygonPos = geoJsonToLeaflet(selectedRegion.polygon);
      const [centerLat, centerLng] = computeCentroid(polygonPos);
      const roleParam = selectedRole ? `&role=${encodeURIComponent(selectedRole.role)}` : "";
      const url = `/admin/plus-attribute-candidate?centerLat=${centerLat}&centerLng=${centerLng}&radius=${radiusKm}&regionId=${selectedRegion._id}&page=1&limit=999999${roleParam}`;
      const res = await getRequestWithToken(url);
      if (fetchId === candidateFetchCounter.current) {
        setAllCandidates(res?.data?.data || []);
        setBackendCounts({
          insideCount: res?.data?.insideCount ?? 0,
          outsideCount: res?.data?.outsideCount ?? 0,
          totalCount: res?.data?.totalCount ?? (res?.data?.data?.length || 0),
        });
        setCandidatesLoaded(true);
      }
    } catch (err) {
      if (fetchId === candidateFetchCounter.current) {
        setError(err?.response?.data?.msg || err?.message || "Failed to load candidates.");
        setAllCandidates([]);
      }
    } finally {
      if (fetchId === candidateFetchCounter.current) setCandidatesLoading(false);
    }
  }, [selectedRegion, selectedRole, radiusKm]);

  // Memoized derived data — recomputed only when dependencies actually change
  const roleOptions = useMemo(
    () => selectedRegion
      ? mappings.filter(m => m.region_id?._id === selectedRegion._id).map(m => m.form_id).filter(Boolean)
      : [],
    [selectedRegion, mappings]
  );

  const polygonPositions = useMemo(
    () => selectedRegion?.polygon ? geoJsonToLeaflet(selectedRegion.polygon) : [],
    [selectedRegion]
  );

  const mapCenter = useMemo(() => getPolygonCenter(polygonPositions), [polygonPositions]);

  const centroid = useMemo(
    () => polygonPositions.length > 0 ? computeCentroid(polygonPositions) : null,
    [polygonPositions]
  );

  // Inside/outside split — uses backend isInsidePolygon flag, no client-side ray-casting
  const { insideMarkers, outsideMarkers } = useMemo(() => {
    const inside = [], outside = [];
    allCandidates.forEach(c => {
      const pos = geoPointToLeaflet(c?.location?.coordinates);
      if (!pos) return;
      (c.isInsidePolygon ? inside : outside).push({ id: c._id, position: pos, role: c.role });
    });
    return { insideMarkers: inside, outsideMarkers: outside };
  }, [allCandidates]);

  // Heatmap coordinate arrays — [lat, lng, intensity]
  const insideHeatPoints = useMemo(
    () => insideMarkers.map(({ position }) => [position[0], position[1], 1]),
    [insideMarkers]
  );
  const outsideHeatPoints = useMemo(
    () => outsideMarkers.map(({ position }) => [position[0], position[1], 1]),
    [outsideMarkers]
  );

  if (!canRead) return <Unauthorized />;
  if (initialLoading) return <ReusableLoader />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)", gap: 2 }}>
      <Typography variant="h5" fontWeight={600}>Region Map View</Typography>

      {/* Filter bar — dropdowns + load button + view toggle */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Autocomplete
          size="small"
          options={regions}
          getOptionLabel={(option) => option.name || ""}
          value={selectedRegion}
          onChange={(_, value) => {
            setSelectedRegion(value);
            setSelectedRole(null);
            setAllCandidates([]);
            setCandidatesLoaded(false);
          }}
          sx={{ minWidth: 340 }}
          renderInput={(params) => <TextField {...params} label="Select Region" size="small" />}
        />

        <Autocomplete
          size="small"
          options={roleOptions}
          getOptionLabel={(option) => option.role || ""}
          value={selectedRole}
          onChange={(_, value) => {
            setSelectedRole(value);
            setAllCandidates([]);
            setCandidatesLoaded(false);
          }}
          disabled={!selectedRegion}
          sx={{ minWidth: 340 }}
          renderInput={(params) => <TextField {...params} label="Filter by Role (optional)" size="small" />}
        />

        {selectedRegion && (
          <Button
            variant="contained"
            size="medium"
            onClick={handleLoadCandidates}
            disabled={candidatesLoading}
            sx={{ whiteSpace: "nowrap" }}
          >
            {candidatesLoading ? "Loading…" : "Load Candidates"}
          </Button>
        )}

        {candidatesLoading && <CircularProgress size={20} />}

        {/* View mode toggle — shown after first load */}
        {candidatesLoaded && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) setViewMode(v); }}
            size="small"
          >
            <ToggleButton value="cluster">Cluster</ToggleButton>
            <ToggleButton value="heatmap">Heatmap</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Radius slider */}
      {selectedRegion && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          <Tooltip title="Expand a circle around the region centroid to find candidates just outside the polygon boundary">
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              Expansion radius: <strong>{radiusKm} km</strong>
            </Typography>
          </Tooltip>
          <Slider
            value={radiusKm}
            min={1}
            max={70}
            step={1}
            marks={RADIUS_MARKS}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v} km`}
            onChange={(_, v) => setRadiusKm(v)}
            sx={{ maxWidth: 420 }}
          />
        </Box>
      )}

      {error && <Typography color="error" variant="body2">{error}</Typography>}

      {/* Stats bar — counts come from backend, no client-side counting */}
      {selectedRegion && candidatesLoaded && !candidatesLoading && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Tooltip title="Candidates whose location falls inside the polygon boundary (classified server-side)">
            <Chip
              label={`✅ Inside polygon: ${backendCounts.insideCount}`}
              sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, border: "1px solid #81c784" }}
            />
          </Tooltip>
          <Tooltip title={`Candidates within ${radiusKm} km of region centroid but outside the polygon`}>
            <Chip
              label={`⚠️ Outside (within ${radiusKm} km): ${backendCounts.outsideCount}`}
              sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600, border: "1px solid #ffb74d" }}
            />
          </Tooltip>
          <Chip label={`Total loaded: ${backendCounts.totalCount}`} variant="outlined" sx={{ fontWeight: 600 }} />
          {backendCounts.totalCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round((backendCounts.insideCount / backendCounts.totalCount) * 100)}% inside polygon
            </Typography>
          )}
        </Box>
      )}

      {/* Map */}
      <Box sx={{ flexGrow: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #e0e0e0" }}>
        <MapContainer center={mapCenter} zoom={selectedRegion ? 10 : 5} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {polygonPositions.length > 0 && (
            <>
              <FitBoundsToPolygon positions={polygonPositions} />
              <Polygon
                positions={polygonPositions}
                pathOptions={{ color: "#1976d2", fillColor: "#42a5f5", fillOpacity: 0.25 }}
              />
            </>
          )}

          {/* Radius circle + centroid dot */}
          {centroid && (
            <>
              <Circle
                center={centroid}
                radius={radiusKm * 1000}
                pathOptions={{ color: "#ff9800", fillColor: "#ffe0b2", fillOpacity: 0.15, dashArray: "6 4" }}
              />
              <CircleMarker
                center={centroid}
                radius={6}
                pathOptions={{ color: "#e65100", fillColor: "#ff9800", fillOpacity: 1, weight: 2 }}
              />
            </>
          )}

          {/* Cluster view — two separate groups keep inside (green) and outside (orange) distinct */}
          {viewMode === "cluster" && (
            <>
              <MarkerClusterGroup chunkedLoading maxClusterRadius={50} iconCreateFunction={insideClusterIcon}>
                {insideMarkers.map(({ id, position, role }) => (
                  <Marker key={`in-${id}`} position={position} icon={createRoleMarkerIcon(role, "#2e7d32")} />
                ))}
              </MarkerClusterGroup>
              <MarkerClusterGroup chunkedLoading maxClusterRadius={50} iconCreateFunction={outsideClusterIcon}>
                {outsideMarkers.map(({ id, position, role }) => (
                  <Marker key={`out-${id}`} position={position} icon={createRoleMarkerIcon(role, "#e65100")} />
                ))}
              </MarkerClusterGroup>
            </>
          )}

          {/* Heatmap view — canvas-based, handles 50K+ points without DOM overhead */}
          {viewMode === "heatmap" && candidatesLoaded && (
            <HeatmapLayer insidePoints={insideHeatPoints} outsidePoints={outsideHeatPoints} />
          )}
        </MapContainer>
      </Box>
    </Box>
  );
};

export default RegionMapView;
